// Razorpay checkout helper
// Loads the Razorpay Checkout script on demand and opens a checkout window
// for the given backend-created order. Resolves with the verified subscription
// details on success, or throws on failure / dismissal.
import api from "@/lib/api";

const SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

let scriptLoading = null;

const loadScript = () => {
  if (typeof window !== "undefined" && window.Razorpay) return Promise.resolve();
  if (scriptLoading) return scriptLoading;
  scriptLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.onload = () => resolve();
    s.onerror = () => {
      scriptLoading = null;
      reject(new Error("Razorpay Checkout script failed to load"));
    };
    document.body.appendChild(s);
  });
  return scriptLoading;
};

/**
 * Run the full upgrade flow.
 * @param {"plus"|"pro"} planCode
 * @param {{name?:string,email?:string,phone?:string}} prefill
 * @returns {Promise<{plan:string, plan_active_until:string}>}
 */
export async function startCheckout(planCode, prefill = {}) {
  await loadScript();

  // 1. Backend creates order
  const orderRes = await api.post("/billing/create-order", { plan: planCode });
  const order = orderRes.data;
  if (!order.key_id) throw new Error("Razorpay is not configured on this server.");

  // 2. Open Razorpay Checkout
  return new Promise((resolve, reject) => {
    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: order.name,
      description: order.description,
      order_id: order.order_id,
      prefill: { ...order.prefill, ...prefill },
      theme: { color: "#3B5BDB" },
      handler: async (response) => {
        try {
          const verify = await api.post("/billing/verify", {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            plan: planCode,
          });
          resolve(verify.data);
        } catch (err) {
          reject(err);
        }
      },
      modal: {
        ondismiss: () => reject(new Error("Payment cancelled")),
      },
      notes: {
        plan: planCode,
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (resp) => {
      reject(new Error(resp?.error?.description || "Payment failed"));
    });
    rzp.open();
  });
}
